import _get from 'lodash/get'
import _kebabCase from 'lodash/kebabCase'
import axios from 'axios'
import { Permissions } from '@constants/permissions'
import router from '@router/index.js'

import { SET_SIGNIN_REDIRECT } from '@constants/mutations'

// Google Auth Mutations
const GOOGLE_AUTH_LOGIN = 'GOOGLE_AUTH_LOGIN'
const GOOGLE_AUTH_LOGOUT = 'GOOGLE_AUTH_LOGOUT'
const GOOGLE_AUTH_ERROR = 'GOOGLE_AUTH_ERROR'
const GOOGLE_AUTH_CLEAR_ERROR = 'GOOGLE_AUTH_CLEAR_ERROR'
const GOOGLE_AUTH_SET_LOADING = 'GOOGLE_AUTH_SET_LOADING'
export const SET_SESSION_TOKEN = 'SET_SESSION_TOKEN'

export default {
  state: {
    // Legacy redirect state
    signInRedirect: false,

    // Consolidated user/auth state (merged from googleAuth)
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
    authTimestamp: null,
    // Signed session token from verifyGoogleLogin; lambdaApi sends it as
    // Authorization: Bearer and heartbeat refreshes it.
    sessionToken: null,
  },
  mutations: {
    [SET_SIGNIN_REDIRECT](state, path) {
      state.signInRedirect = path
    },

    // Google Auth mutations (moved from googleAuth.js)
    [GOOGLE_AUTH_LOGIN](state, payload) {
      const { user, timestamp = Date.now(), sessionToken = null } = payload
      state.user = user
      state.isAuthenticated = true
      state.error = null
      state.authTimestamp = timestamp
      state.sessionToken = sessionToken
    },

    [GOOGLE_AUTH_LOGOUT](state) {
      state.user = null
      state.isAuthenticated = false
      state.error = null
      state.authTimestamp = null
      state.sessionToken = null
    },

    [SET_SESSION_TOKEN](state, sessionToken) {
      state.sessionToken = sessionToken
    },

    [GOOGLE_AUTH_ERROR](state, error) {
      state.error = error
      state.isLoading = false
    },

    [GOOGLE_AUTH_CLEAR_ERROR](state) {
      state.error = null
    },

    [GOOGLE_AUTH_SET_LOADING](state, loading) {
      state.isLoading = loading
    },
  },
  actions: {
    // Google Auth verification (moved from googleAuth.js)
    async verifyGoogleAuth(
      { commit },
      { code, codeVerifier, state: oauthState }
    ) {
      commit(GOOGLE_AUTH_SET_LOADING, true)
      commit(GOOGLE_AUTH_CLEAR_ERROR)

      try {
        console.log(
          '🔍 GoogleAuth: Starting verification with code:',
          code?.substring(0, 10) + '...'
        )

        // Call our lambda function to verify the Google OAuth code
        const response = await axios.post(
          '/.netlify/functions/verifyGoogleLogin',
          {
            code,
            codeVerifier,
            redirectUri: window.location.origin + '/auth/callback',
            state: oauthState,
          },
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )

        console.log('🔍 GoogleAuth: Response status:', response.status)
        console.log('🔍 GoogleAuth: Response data:', response.data)

        if (response.data.success && response.data.payload) {
          const user = response.data.payload
          console.log('🔍 GoogleAuth: User data received:', user)

          // Store the authenticated user data
          commit(GOOGLE_AUTH_LOGIN, {
            user,
            timestamp: Date.now(),
            sessionToken: response.data.sessionToken,
          })

          console.log('✅ GoogleAuth: User stored in Vuex successfully')
          return { success: true, user }
        } else {
          throw new Error('Authentication verification failed')
        }
      } catch (error) {
        console.error('❌ GoogleAuth: Error during verification:', error)
        console.error('❌ GoogleAuth: Error response:', error.response?.data)

        const errorMessage =
          error.response?.data?.details ||
          error.message ||
          'Authentication failed'
        commit(GOOGLE_AUTH_ERROR, errorMessage)
        return { success: false, error: errorMessage }
      } finally {
        commit(GOOGLE_AUTH_SET_LOADING, false)
      }
    },

    logout({ commit }) {
      commit(GOOGLE_AUTH_LOGOUT)
    },

    clearError({ commit }) {
      commit(GOOGLE_AUTH_CLEAR_ERROR)
    },

    // Legacy logout with navigation
    async logOut({ commit }) {
      // Clear authentication
      commit(GOOGLE_AUTH_LOGOUT)

      // Navigate to login page - the router guard will handle the redirect logic
      router.push('/')
    },
  }, // actions
  getters: {
    // Legacy redirect state
    signInRedirect: (state) => state.signInRedirect,

    // Basic auth state (consolidated from googleAuth)
    isAuthenticated: (state) => state.isAuthenticated,
    user: (state) => state.user,
    sessionToken: (state) => state.sessionToken,
    isLoading: (state) => state.isLoading,
    error: (state) => state.error,

    // User information
    userEmail: (state) => state.user?.email || '',
    userName: (state) => state.user?.name || '',
    userGivenName: (state) => state.user?.given_name || '',
    userFamilyName: (state) => state.user?.family_name || '',
    isEmailVerified: (state) => state.user?.email_verified || false,

    // User data structure from Google Directory API
    userPermissions: (state) => {
      // Array of permission strings from Google custom attributes
      // Return empty array if user not authenticated or no custom attributes
      if (!state.user?.customAttributes) {
        return []
      }

      const customAttrs = state.user.customAttributes
      const googlePerms = customAttrs.permissions || []

      // Parse permissions objects to extract the value strings
      if (Array.isArray(googlePerms)) {
        return googlePerms
          .filter((perm) => perm && typeof perm === 'object' && perm.value)
          .map((perm) => perm.value)
      }

      // Handle single permission object
      if (googlePerms && typeof googlePerms === 'object' && googlePerms.value) {
        return [googlePerms.value]
      }

      return []
    },
    userMetadata: (state) => state.user?.metadata || {},
    customAttributes: (state) => state.user?.customAttributes || {},

    // Helper getter to check if user has a specific permission(s) or is admin
    hasPermission: (state, getters) => (permissionKeys) => {
      // Admin always has all permissions
      if (
        getters.userPermissions.includes('admin') ||
        getters.userPermissions.includes('administrator') ||
        getters.userPermissions.includes('super_admin')
      ) {
        return true
      }

      // Handle single permission or array of permissions
      const permissions = Array.isArray(permissionKeys) ? permissionKeys : [permissionKeys]
      
      // Check if user has any of the specified permissions (direct string matching)
      return permissions.some(permissionKey => {
        return getters.userPermissions.includes(permissionKey)
      })
    },

    // Permission-based access checks (consolidated from googleAuth)
    isAdmin: (_, getters) => getters.hasPermission(['admin', Permissions.ADMIN]),
    isAccountant: (_, getters) => getters.hasPermission(Permissions.ACCOUNTANT),
    isForeman: (_, getters) => getters.hasPermission(Permissions.FOREMAN),
    isDispatcher: (_, getters) => getters.hasPermission(Permissions.DISPATCHER),
    isProjMgr: (_, getters) =>
      getters.hasPermission(Permissions.PROJECT_MANAGER),
    isEmployee: (_, getters) => getters.hasPermission(Permissions.EMPLOYEE),

    // Enhanced permission checks (role-based + granular permissions)
    canViewScheduler: (_, getters) =>
      getters.hasPermission([Permissions.PROJECT_MANAGER, Permissions.FOREMAN, Permissions.SCHEDULER_VIEW, Permissions.SCHEDULER_EDIT]),

    canEditScheduler: (_, getters) =>
      getters.hasPermission([Permissions.PROJECT_MANAGER, Permissions.SCHEDULER_EDIT]),


    canAccessScheduler: (state, getters) =>
      getters.canViewScheduler || getters.canEditScheduler,

    canViewShiftItems: (state, getters, _, rootGetters) => {
      let canForemanView =
        getters.isForeman &&
        !_get(rootGetters['getLocation'], 'JOB_TYPE', []).includes('Fiber')

      return getters.isProjMgr || canForemanView || getters.isAdmin
    },

    canEditShiftItems: (state, getters) =>
      getters.isProjMgr || getters.isForeman || getters.isAdmin,

    canAddRemoveShiftItems: (state, getters) =>
      getters.isProjMgr || getters.isAdmin,

    // Authentication metadata
    authAge: (state) => {
      if (!state.authTimestamp) return null
      return Date.now() - state.authTimestamp
    },
    authAgeMinutes: (state, getters) => {
      const age = getters.authAge
      return age ? Math.floor(age / (1000 * 60)) : 0
    },

    // Group membership checks
    isInGroup: (state) => (groupEmail) => {
      return (
        state.user?.groups?.some((group) => group.email === groupEmail) || false
      )
    },

    // Display helpers
    displayName: (state, getters) => {
      if (getters.userName) return getters.userName
      if (getters.userGivenName && getters.userFamilyName) {
        return `${getters.userGivenName} ${getters.userFamilyName}`
      }
      return getters.userEmail
    },

    // Legacy user info getters (backward compatibility)
    getUserDisplayName: (state, getters) => getters.displayName || 'User Name',
    getUserEmail: (state, getters) => getters.userEmail,
    firstName: (state, getters) => getters.userGivenName,
    lastName: (state, getters) => getters.userFamilyName,

    // Slugified CSS permission classes for QA and styling
    cssPermissionClasses: (state, getters) => {
      const classes = []

      // Role-based classes
      if (getters.isAdmin) classes.push('permission-admin')
      if (getters.isProjMgr) classes.push('permission-project-manager')
      if (getters.isAccountant) classes.push('permission-accountant')
      if (getters.isForeman) classes.push('permission-foreman')
      if (getters.isDispatcher) classes.push('permission-dispatcher')
      if (getters.isEmployee) classes.push('permission-employee')

      // Capability-based classes
      if (getters.canViewScheduler) classes.push('can-view-scheduler')
      if (getters.canEditScheduler) classes.push('can-edit-scheduler')

      // Raw permission classes (slugified for CSS)
      getters.userPermissions.forEach((permission) => {
        classes.push(`permission-${_kebabCase(permission)}`)
      })

      return classes
    },

  },
}
